"use client"

import { useCallback, useEffect, useState } from "react"

const DEFAULT_OPTIONS = {
  interval: 5000,
  fetchOnMount: true,
} as const

type Options = {
  interval: number
  fetchOnMount: boolean
}

export function usePolling<T>(initialData: T, fn: () => Promise<T>, _options: Partial<Options> = DEFAULT_OPTIONS) {
  const options = { ...DEFAULT_OPTIONS, ..._options }
  const [data, setData] = useState<T>(initialData)

  useEffect(() => {
    if (options.fetchOnMount) {
      fn().then(setData)
    }
  }, [])

  useEffect(() => {
    if (options.interval !== 0) {
      const intervalId = setInterval(async () => {
        const _data = await fn()
        setData(_data)
      }, options.interval)
      return () => clearInterval(intervalId)
    }
  }, [fn, options.interval, setData])

  const invalidate = useCallback(
    async (newData?: T) => {
      const _data = newData ?? (await fn())
      setData(_data)
    },
    [fn]
  )
  return { data, invalidate }
}
